import React, { useState, useEffect } from 'react';
import './GlobalDashboard.css';
import { API_URL } from '../config';

interface GlobalStats {
  total_communes: number;
  total_projects: number;
  total_roads: number;
  total_sections: number;
  total_length: number;
  total_surface: number;
  average_condition: number;
  communes_summary: Array<{
    name: string;
    roads_count: number;
    total_length: number;
    average_condition: number;
  }>;
}

interface GlobalDashboardProps {
  onViewProjects?: () => void;
  onViewCommunes?: () => void;
  onCreateProject?: () => void;
}

const GlobalDashboard: React.FC<GlobalDashboardProps> = ({ onViewProjects, onViewCommunes, onCreateProject }) => {
  const [stats, setStats] = useState<GlobalStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [communes, setCommunes] = useState<string[]>([]);
  const [filteredCommunes, setFilteredCommunes] = useState<string[]>([]);
  const [showCommuneDropdown, setShowCommuneDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState({
    commune: '',
    traffic: '',
    structure: ''
  });

  useEffect(() => {
    loadCommunes();
    loadGlobalStats();
  }, [filter]);

  const loadCommunes = async () => {
    try {
      const response = await fetch(`${API_URL}/communes`);
      const data = await response.json();
      const communeNames = data.map((c: any) => c.name);
      setCommunes(communeNames);
      setFilteredCommunes(communeNames);
    } catch (error) {
      console.error('Error loading communes:', error);
    }
  };

  const handleCommuneSearch = (term: string) => {
    setSearchTerm(term);
    const filtered = communes.filter(commune => 
      commune.toLowerCase().includes(term.toLowerCase())
    );
    setFilteredCommunes(filtered);
    setShowCommuneDropdown(term.length > 0 && filtered.length > 0);
  };

  const selectCommune = (commune: string) => {
    setSearchTerm(commune);
    setFilter({...filter, commune});
    setShowCommuneDropdown(false);
  };

  const clearCommuneSearch = () => {
    setSearchTerm('');
    setFilter({...filter, commune: ''});
    setShowCommuneDropdown(false);
    setFilteredCommunes(communes);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.commune-search-wrapper')) {
        setShowCommuneDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadGlobalStats = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter.commune) params.append('commune', filter.commune);
      if (filter.traffic) params.append('traffic', filter.traffic);
      if (filter.structure) params.append('structure', filter.structure);
      
      const response = await fetch(`${API_URL}/global-stats?${params}`);
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error loading global stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="global-dashboard loading">
        <div className="loading-spinner"></div>
        <p>Chargement des statistiques...</p>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="global-dashboard error">
        <p>Erreur lors du chargement des données</p>
      </div>
    );
  }

  return (
    <div className="global-dashboard">
      <div className="dashboard-header">
        <h1>📊 Dashboard</h1>
        <p>Vue d'ensemble du réseau routier</p>
      </div>

      <div className="filters-section">
        <h3>🔍 Filtres</h3>
        <div className="filters-grid">
          <div className="commune-search-wrapper">
            <input
              type="text"
              placeholder="Rechercher une commune..."
              value={searchTerm}
              onChange={(e) => handleCommuneSearch(e.target.value)}
              onFocus={() => searchTerm.length > 0 && setShowCommuneDropdown(true)}
              className="commune-search"
            />
            {searchTerm && (
              <button 
                className="clear-commune"
                onClick={clearCommuneSearch}
              >
                ✕
              </button>
            )}
            {showCommuneDropdown && (
              <div className="commune-dropdown">
                {filteredCommunes.map((commune) => (
                  <div
                    key={commune}
                    className="commune-option"
                    onClick={() => selectCommune(commune)}
                  >
                    {commune}
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <select 
            value={filter.traffic} 
            onChange={(e) => setFilter({...filter, traffic: e.target.value})}
          >
            <option value="">Tous les trafics</option>
            <option value="T0">T0 - Très faible</option>
            <option value="T1">T1 - Faible</option>
            <option value="T2">T2 - Moyen</option>
            <option value="T3">T3 - Fort</option>
          </select>
          
          <select 
            value={filter.structure} 
            onChange={(e) => setFilter({...filter, structure: e.target.value})}
          >
            <option value="">Toutes les structures</option>
            <option value="E">Enrobé</option>
            <option value="B">Béton</option>
            <option value="S">Souple</option>
            <option value="M">Mixte</option>
          </select>
        </div>
      </div>

      <div className="stats-overview">
        <div className="stat-card">
          <div className="stat-icon">🏘️</div>
          <div className="stat-content">
            <h3>{stats.total_communes}</h3>
            <p>Communes</p>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon">📁</div>
          <div className="stat-content">
            <h3>{stats.total_projects || 0}</h3>
            <p>Projets</p>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon">🛣️</div>
          <div className="stat-content">
            <h3>{stats.total_roads}</h3>
            <p>Routes</p>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon">📏</div>
          <div className="stat-content">
            <h3>{stats.total_sections}</h3>
            <p>Tronçons</p>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon">📐</div>
          <div className="stat-content">
            <h3>{((stats.total_length || 0) / 1000).toFixed(1)} km</h3>
            <p>Longueur totale</p>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon">📊</div>
          <div className="stat-content">
            <h3>{((stats.total_surface || 0) / 10000).toFixed(1)} ha</h3>
            <p>Surface totale</p>
          </div>
        </div>
        
        <div className="stat-card condition">
          <div className="stat-icon">🎯</div>
          <div className="stat-content">
            <h3 style={{ color: getConditionColor(stats.average_condition) }}>
              {(stats.average_condition || 0).toFixed(2)}
            </h3>
            <p>État moyen</p>
          </div>
        </div>
      </div>

      <div className="communes-summary">
        <h3>📍 Résumé par Commune</h3>
        <div className="communes-table">
          <div className="table-header">
            <span>Commune</span>
            <span>Routes</span>
            <span>Longueur (km)</span>
            <span>État moyen</span>
          </div>
          {(stats.communes_summary || []).map((commune, index) => (
            <div key={index} className="table-row">
              <span className="commune-name">{commune.name}</span>
              <span>{commune.roads_count}</span>
              <span>{((commune.total_length || 0) / 1000).toFixed(1)}</span>
              <span 
                className="condition-score"
                style={{ color: getConditionColor(commune.average_condition) }}
              >
                {(commune.average_condition || 0).toFixed(2)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const getConditionColor = (score: number) => {
  if (score >= 4) return '#4CAF50';
  if (score >= 3) return '#FF9800';
  if (score >= 2) return '#FF5722';
  return '#F44336';
};

export default GlobalDashboard;